<template>
    <div class="animated fadeIn">
        <div>
            <div v-if="errorMessage" class="error-message-block" role="alert">
                <p class="error-message-text">{{ errorMessage }}</p>
                <p class="error-message-hint">You can go back and try again.</p>
                <button
                    type="button"
                    class="btn btn-primary error-redirect-btn"
                    @click="redirectFromError"
                >
                    Return to previous page
                </button>
            </div>

            <div v-if="!errorMessage" class="stripeBox">
                <div
                    v-if="isLoading"
                    class="paymentLoaderOverlay"
                    role="status"
                    aria-live="polite"
                >
                    <div class="paymentSpinner" aria-hidden="true"></div>
                    <div class="paymentLoaderText">Loading...</div>
                </div>

                <div
                    v-if="showUpgradePriceMessage"
                    class="upgrade-price-message"
                    role="status"
                >
                    <div class="upgrade-price-message__header">
                        <span class="upgrade-price-message__icon">ℹ</span>
                        <span>Plan upgrade – Price adjustment</span>
                    </div>
                    <div class="upgrade-price-message__body">
                        <div class="upgrade-price-message__row">
                            <span class="upgrade-price-message__label">Plan Price</span>
                            <span class="upgrade-price-message__value">{{ currencySymbol }}{{ formattedPlanPrice }}</span>
                        </div>
                        <div class="upgrade-price-message__row">
                            <span class="upgrade-price-message__label">Total Tax</span>
                            <span class="upgrade-price-message__value">
                                +{{ currencySymbol }}{{ formattedTotalTaxAmount }}
                            </span>
                        </div>
                        <div class="upgrade-price-message__row upgrade-price-message__row--subtract">
                            <span class="upgrade-price-message__label">Adjusted Amount</span>
                            <span class="upgrade-price-message__value upgrade-price-message__value--subtract">
                                −{{ currencySymbol }}{{ formattedAdjustedAmount }}
                            </span>
                        </div>
                        <div class="upgrade-price-message__row upgrade-price-message__row--highlight">
                            <span class="upgrade-price-message__label">Payable amount</span>
                            <span class="upgrade-price-message__value">{{ currencySymbol }}{{ formattedPayableAmount }}</span>
                        </div>
                    </div>
                </div>

                <div
                    v-show="!isLoading"
                    id="stripe-payment-element"
                    ref="stripeMount"
                ></div>
            </div>
        </div>
    </div>
</template>

<script>
import { STRIPE_PUBLISHABLE_KEY } from "@/config";

const STRIPE_JS_SRC = "https://js.stripe.com/v3/";
const STORAGE_KEY_PREFIX = "stripePaymentInitPayload_";
const CURRENCY_SYMBOLS = Object.freeze({
    USD: "$",
    INR: "₹",
    EUR: "€",
    GBP: "£",
});

function parseQueryJson(raw) {
    if (!raw || typeof raw !== "string") return null;
    try {
        return JSON.parse(decodeURIComponent(raw));
    } catch {
        return null;
    }
}

function parseQueryBoolean(raw) {
    if (raw === "true" || raw === true) return true;
    if (raw === "false" || raw === false) return false;
    return null;
}

function safeNumber(value, fallback) {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
}

export default {
    name: "PaymentInit",
    data() {
        return {
            paymentId: this.$route.params.id,
            isLoading: false,
            errorMessage: "",
            adjustedAmount: 0,
            payableAmount: 0,
            totalTaxAmount: 0,

            // Stripe instances
            stripe: null,
            elements: null,
            paymentElement: null,
            embeddedCheckout: null,

            clientSecret: "",
            isEmbeddedCheckout: false,
        };
    },
    computed: {
        stripePublishableKey() {
            return STRIPE_PUBLISHABLE_KEY || "";
        },
        initStorageKey() {
            return `${STORAGE_KEY_PREFIX}${this.paymentId}`;
        },
        currency() {
            return (this.$route.query.currency || "USD").toString();
        },
        qty() {
            const v = safeNumber(this.$route.query.qty, NaN);
            return Number.isFinite(v) && v > 0 ? v : 1;
        },
        planId() {
            return (this.paymentId || "").toString();
        },
        tenant() {
            const fromQuery = this.$route.query.tenant;
            if (fromQuery) return String(fromQuery);
            return "";
        },
        productName() {
            return (this.$route.query.productName || "Subscription").toString();
        },
        notifyUrl() {
            return (this.$route.query.notifyUrl || "").toString();
        },
        amount() {
            return safeNumber(this.$route.query.amount, 500);
        },
        metadataFromQuery() {
            return parseQueryJson(this.$route.query.metadata);
        },
        userFromQuery() {
            return parseQueryJson(this.$route.query.user);
        },
        billingAddressFromQuery() {
            return parseQueryJson(this.$route.query.billingAddress);
        },
        verifyReturnUrl() {
            return `${window.location.origin}${window.location.pathname}#/payment/verify/${this.paymentId}`;
        },
        upgrade() {
            return parseQueryBoolean(this.$route.query.upgrade);
        },
        isUpdate() {
            return this.upgrade;
        },
        showUpgradePriceMessage() {
            return (
                this.isUpdate &&
                this.adjustedAmount != null &&
                Number(this.adjustedAmount) !== 0
            );
        },
        formattedPlanPrice() {
            const amt = Number(this.amount);
            return Number.isFinite(amt) ? amt.toFixed(2) : "0.00";
        },
        formattedPayableAmount() {
            const amt = Number(this.payableAmount);
            return Number.isFinite(amt) ? amt.toFixed(2) : "0.00";
        },
        formattedAdjustedAmount() {
            const amt = Number(this.adjustedAmount);
            return Number.isFinite(amt) ? amt.toFixed(2) : "0.00";
        },
        formattedTotalTaxAmount() {
            const amt = Number(this.totalTaxAmount);
            return Number.isFinite(amt) ? amt.toFixed(2) : "0.00";
        },
        currencySymbol() {
            const c = (this.currency || "USD").toUpperCase();
            return CURRENCY_SYMBOLS[c] || `${c} `;
        },
        isInvoice() {
            return parseQueryBoolean(this.$route.query.invoice) === true;
        },
    },
    async mounted() {
        await this.initStripe();
    },
    beforeDestroy() {
        this.cleanupStripe();
    },
    methods: {
        async initStripe() {
            this.clearInitPayload();
            this.errorMessage = "";
            this.isLoading = true;

            try {
                await this.ensureStripeJsLoaded();

                const pk = this.stripePublishableKey;
                if (!pk) {
                    throw new Error(
                        "Stripe publishable key missing. Set VUE_APP_STRIPE_PUBLISHABLE_KEY in your environment.",
                    );
                }

                this.stripe = window.Stripe(pk);
                if (!this.stripe) throw new Error("Failed to initialize Stripe.");

                const storagePayload = this.buildPaymentRequestPayload();
                const apiPayload = this.isInvoice
                    ? this.buildInvoicePaymentRequestPayload()
                    : storagePayload;

                sessionStorage.setItem(
                    this.initStorageKey,
                    JSON.stringify(storagePayload),
                );

                const resp = this.isInvoice
                    ? await this.$store.dispatch(
                          "subscription/initInvoiceStripePaymentAction",
                          {
                              id: String(this.$route.query.invoiceId || ""),
                              data: apiPayload,
                          },
                      )
                    : await this.$store.dispatch(
                          "subscription/initStripePaymentAction",
                          apiPayload,
                      );

                if (!resp || resp.status !== 1) {
                    throw new Error(
                        resp && (resp.message || resp.msg)
                            ? resp.message || resp.msg
                            : "Payment init failed.",
                    );
                }
                const providerPaymentId = this.extractProviderPaymentId(resp);
                const payableAmount = this.extractPayableAmount(resp);
                const adjustedAmount = this.extractAdjustedAmount(resp);
                const totalTaxAmount = this.extractTotalTaxAmount(resp);

                this.adjustedAmount = adjustedAmount;
                this.payableAmount = payableAmount;
                this.totalTaxAmount = totalTaxAmount;

                const storedPayload = JSON.parse(
                    sessionStorage.getItem(
                        this.initStorageKey,
                    ) || "{}",
                );

                if (
                    payableAmount !== null &&
                    payableAmount !== undefined &&
                    payableAmount !== ""
                ) {
                    const numericPayable = Number(payableAmount);
                    storedPayload.amount = Number.isFinite(numericPayable)
                        ? numericPayable
                        : payableAmount;
                }

                if (providerPaymentId) {
                    storedPayload.providerPaymentId = providerPaymentId;
                }

                sessionStorage.setItem(
                    this.initStorageKey,
                    JSON.stringify(storedPayload),
                );

                const secret = this.extractClientSecret(resp);
                if (!secret) {
                    throw new Error("client_secret not received from backend.");
                }

                this.clientSecret = decodeURIComponent(secret);
                await this.mountStripeUi(this.clientSecret);
            } catch (err) {
                this.errorMessage =
                    (err && err.message) ||
                    "Something went wrong while initializing Stripe.";
            } finally {
                this.isLoading = false;
            }
        },
        buildPaymentRequestPayload() {
            const urlUser = this.userFromQuery;
            const urlMetadata = this.metadataFromQuery;

            const metadata =
                urlMetadata && typeof urlMetadata === "object"
                    ? urlMetadata
                    : { planId: this.planId, user: this.tenant };
            const userPayload =
                urlUser && typeof urlUser === "object"
                    ? {
                          id: String(urlUser.id || ""),
                          name: String(urlUser.name || ""),
                      }
                    : { id: "", name: "" };

            const payload = {
                amount: this.amount !== null ? Number(this.amount) : 0,
                currency: this.currency,
                qty: this.qty,
                planId: this.planId,
                tenant: this.tenant,
                provider: "STRIPE",
                metadata,
                returnUrl: this.verifyReturnUrl,
                platform: "web",
                user: userPayload,
                paymentType: "STRIPE",
                productName: this.productName,
            };

            if (this.notifyUrl) payload.notifyUrl = this.notifyUrl;
            if (this.upgrade !== null) payload.upgrade = this.upgrade;

            const billingAddress = this.billingAddressFromQuery;
            if (billingAddress && typeof billingAddress === "object") {
                payload.billingAddress = billingAddress;
            }

            return payload;
        },

        buildInvoicePaymentRequestPayload() {
            const urlUser = this.userFromQuery;
            const userPayload =
                urlUser && typeof urlUser === "object"
                    ? {
                          id: String(urlUser.id || ""),
                          name: String(urlUser.name || ""),
                          email: String(urlUser.email || ""),
                          phone: String(urlUser.phone || ""),
                      }
                    : { id: "", name: "", email: "", phone: "" };

            const payload = {
                paymentType: "STRIPE",
                provider: "STRIPE",
                tenant: this.tenant,
                platform: "web",
                returnUrl: "https://seoai-360-dev.cwdd.me/verify-payment",
                notifyUrl: this.notifyUrl || "",
                user: userPayload,
                metadata: {
                    source: "partner-portal",
                    tenant: this.tenant,
                },
                qty: this.qty,
                productName: this.productName || "Subscription Invoice Payment",
            };

            const billingAddress = this.billingAddressFromQuery;
            if (billingAddress && typeof billingAddress === "object") {
                payload.billingAddress = billingAddress;
            }

            return payload;
        },

        extractClientSecret(resp) {
            const r = resp && resp.response;
            if (this.isInvoice) {
                return (
                    (r &&
                        r.payment &&
                        r.payment.raw &&
                        r.payment.raw.res &&
                        r.payment.raw.res.client_secret) ||
                    ""
                );
            }
            return (r && r.raw && r.raw.res && r.raw.res.client_secret) || "";
        },

        extractProviderPaymentId(resp) {
            const r = resp && resp.response;
            if (this.isInvoice) {
                return (r && r.paymentOrderId) || "";
            }
            return (r && r.providerPaymentId) || "";
        },

        extractPayableAmount(resp) {
            const r = resp && resp.response;
            if (this.isInvoice) return (r && r.amount) || 0;
            return (r && r.payableAmount) || 0;
        },
        extractAdjustedAmount(resp) {
            const r = resp && resp.response;
            return (r && r.adjusted) || 0;
        },
        extractTotalTaxAmount(resp) {
            const r = resp && resp.response;
            return (r && r.totalTaxes) || 0;
        },
        clearInitPayload() {
            try {
                sessionStorage.removeItem(
                    this.initStorageKey,
                );
            } catch (e) {
                //
            }
        },

        async mountStripeUi(clientSecret) {
            await this.$nextTick();
            this.cleanupStripe();

            const mountEl = this.$refs.stripeMount;
            if (!mountEl) throw new Error("Stripe mount element not found.");
            mountEl.innerHTML = "";

            // Embedded Checkout (Checkout Session client secret starts with cs_)
            if (
                typeof clientSecret === "string" &&
                clientSecret.startsWith("cs_") &&
                this.stripe &&
                typeof this.stripe.initEmbeddedCheckout === "function"
            ) {
                this.isEmbeddedCheckout = true;
                this.embeddedCheckout = await this.stripe.initEmbeddedCheckout({
                    clientSecret,
                    onComplete: () => {
                        this.cleanupStripe();
                        this.redirectToExternalPayment();
                    },
                });
                await this.embeddedCheckout.mount("#stripe-payment-element");
                return;
            }

            this.isEmbeddedCheckout = false;
            this.elements = this.stripe.elements({ clientSecret });
            this.paymentElement = this.elements.create("payment");
            this.paymentElement.mount(mountEl);
        },

        redirectFromError() {
            if (window.history.length > 1) {
                this.$router.go(-1);
            } else {
                this.$router.push("/");
            }
        },
        redirectToExternalPayment() {
            try {
                const storedPayload = sessionStorage.getItem(
                    this.initStorageKey,
                );
                const payload = storedPayload ? JSON.parse(storedPayload) : {};
                const base =
                    this.$route.query.platform === "prod"
                        ? "https://seoai360.com"
                        : "https://seoai-360-dev.cwdd.me";

                if (this.isInvoice === true) {
                    const invoiceId = String(this.$route.query.invoiceId || "");
                    const paymentOrderId = String(payload.providerPaymentId || "");

                    const invoiceQueryString = [
                        invoiceId
                            ? `invoiceId=${encodeURIComponent(invoiceId)}`
                            : null,
                        paymentOrderId
                            ? `paymentOrderId=${encodeURIComponent(paymentOrderId)}`
                            : null,
                    ]
                        .filter(Boolean)
                        .join("&");

                    const redirectUrl = `${base}/verify-invoice-payment${
                        invoiceQueryString ? `?${invoiceQueryString}` : ""
                    }`;
                    window.location.href = redirectUrl;
                    return;
                }

                const finalPayload = payload;

                const queryString = Object.keys(finalPayload)
                    .map((key) => {
                        const value = finalPayload[key];
                        if (value === null || value === undefined) return null;
                        if (typeof value === "object") {
                            return `${encodeURIComponent(key)}=${encodeURIComponent(JSON.stringify(value))}`;
                        }
                        return `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`;
                    })
                    .filter((param) => param !== null)
                    .join("&");
                const redirectUrl = `${base}/verify-payment${queryString ? `?${queryString}` : ""}`;
                window.location.href = redirectUrl;
            } catch (e) {
                console.error("Error redirecting to external payment:", e);
            }
        },

        cleanupStripe() {
            try {
                const safeInvoke = (obj, methodName) => {
                    try {
                        const fn = obj && obj[methodName];
                        if (typeof fn === "function") fn.call(obj);
                    } catch (e) {}
                };

                if (this.embeddedCheckout) {
                    safeInvoke(this.embeddedCheckout, "unmount");
                    safeInvoke(this.embeddedCheckout, "destroy");
                    this.embeddedCheckout = null;
                }

                if (this.paymentElement) {
                    safeInvoke(this.paymentElement, "unmount");
                    this.paymentElement = null;
                }

                this.elements = null;
            } catch (e) {}
        },

        ensureStripeJsLoaded() {
            return new Promise((resolve, reject) => {
                if (window.Stripe) return resolve(true);

                const existing = document.querySelector(
                    `script[src="${STRIPE_JS_SRC}"]`,
                );
                if (existing) {
                    existing.addEventListener("load", () => resolve(true));
                    existing.addEventListener("error", () =>
                        reject(new Error("Failed to load Stripe.js")),
                    );
                    return;
                }

                const script = document.createElement("script");
                script.src = STRIPE_JS_SRC;
                script.async = true;
                script.onload = () => resolve(true);
                script.onerror = () =>
                    reject(new Error("Failed to load Stripe.js"));
                document.head.appendChild(script);
            });
        },
    },
};
</script>

<style>
.error-message-block {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 2rem 1.5rem;
    margin: 1rem 0;
    border: 1px solid #f5c6cb;
    border-radius: 8px;
    background: #f8d7da;
    color: #721c24;
}

.error-message-text {
    margin: 0 0 0.5rem;
    font-weight: 600;
    font-size: 1rem;
}

.error-message-hint {
    margin: 0 0 1.25rem;
    font-size: 0.9rem;
    color: #856404;
}

.error-redirect-btn {
    min-width: 180px;
}

.stripeBox {
    position: relative;
    padding: 20px;
    border: 1px solid #e5e7eb;
    border-radius: 5px;
    background: #fff;
    min-height: 100vh;
}

.paymentLoaderOverlay {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 24px;
    background: rgba(255, 255, 255, 0.88);
    border-radius: 5px;
}

.paymentSpinner {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    border: 5px solid rgba(0, 0, 0, 0.08);
    border-top-color: #3a49f9;
    animation: paymentSpin 0.9s linear infinite;
}

.paymentLoaderText {
    margin-top: 12px;
    color: #374151;
    font-weight: 600;
    font-size: 14px;
}

@keyframes paymentSpin {
    to {
        transform: rotate(360deg);
    }
}

.upgrade-price-message {
    max-width: 1000px;
    margin-inline: auto;
    margin-bottom: 1.25rem;
    padding: 1rem 1.25rem;
    background: linear-gradient(135deg, #f0f7ff 0%, #e8f4fd 100%);
    border: 1px solid #b8d4f0;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(58, 73, 249, 0.08);
}

.upgrade-price-message__header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
    font-weight: 600;
    font-size: 0.95rem;
    color: #1e3a5f;
}

.upgrade-price-message__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.25rem;
    height: 1.25rem;
    background: #3a49f9;
    color: #fff;
    border-radius: 50%;
    font-size: 0.75rem;
    font-style: normal;
}

.upgrade-price-message__body {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.upgrade-price-message__row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.4rem 0;
    font-size: 0.9rem;
}

.upgrade-price-message__row--subtract .upgrade-price-message__value--subtract {
    color: red;
    font-weight: 600;
}

.upgrade-price-message__row--highlight {
    padding: 0.5rem 0.75rem;
    margin: 0 -0.75rem -0.25rem;
    background: rgba(58, 73, 249, 0.08);
    border-radius: 6px;
    font-weight: 600;
    color: #1e3a5f;
}

.upgrade-price-message__label {
    color: #4a5568;
}

.upgrade-price-message__value {
    font-weight: 600;
    color: #2d3748;
}
</style>